/**
 * DefenseBuilderPage
 *
 * Lets a coach build a custom defense: click a defender on the field, assign him a
 * coverage responsibility from a dropdown, repeat for all 7 positions, then save the
 * result to the backend. Layout mirrors SimulationPage (field + sticky sidebar).
 *
 * Scalability: the per-position dropdown options come entirely from
 * src/rules/positionAssignments.ts. Adding a new coverage or turning on a new position's
 * behavior only requires updating that registry — this page needs no changes.
 */

import { useState } from 'react';
import { DefensePositionId, FormationType, CustomDefenseAssignments, CoverageType } from '../types';
import {
  DEFENSE_POSITION_IDS,
  DEFENSE_POSITION_LABELS,
  getAssignmentOption,
  getDefaultAssignments,
} from '../rules';
import { DefenseBuilderField, PositionAssignmentSelector, FormationSelector, CustomDefenseManager } from '../components';
import type { DefensePayload } from '../components/CustomDefenseManager';
import { useSaveTarget, usePremiumStatus } from '../hooks';
import './DefenseBuilderPage.css';

function DefenseBuilderPage() {
  const { isPremium, isLoading: authLoading } = usePremiumStatus();
  const [formation, setFormation] = useState<FormationType>('doubles');
  const [assignments, setAssignments] = useState<CustomDefenseAssignments>(getDefaultAssignments());
  const [selectedPosition, setSelectedPosition] = useState<DefensePositionId>('cb1');

  const currentDefense: DefensePayload = { formation, assignments };
  // Which saved defense the board came from, so saving an edit can offer to update it.
  const defenseSaveTarget = useSaveTarget(currentDefense);

  const handleAssignmentChange = (coverage: CoverageType) => {
    setAssignments((prev) => ({ ...prev, [selectedPosition]: coverage }));
  };

  const handleLoadDefense = (defense: DefensePayload) => {
    setFormation(defense.formation);
    setAssignments(defense.assignments);
    setSelectedPosition('cb1');
  };

  const selectedOption = getAssignmentOption(selectedPosition, assignments[selectedPosition]);

  if (authLoading) return null;

  // The Simulator's entry point to this page is locked for non-premium users, so the page itself
  // has to agree — otherwise the lock is decoration that a typed URL walks straight past, and a
  // free user would build a whole defense before discovering they cannot keep it.
  if (!isPremium) {
    return (
      <div className="defense-builder-page defense-builder-locked-page">
        <div className="builder-locked">
          <h2>🔒 Defense Play Maker</h2>
          <p>
            Build your own coverage from scratch — assign every defender his own responsibility,
            then run it against any route combination in the Simulator.
          </p>
          <p>This is a Premium feature.</p>
          <a className="builder-locked-cta" href="/pricing">See Premium plans</a>
        </div>
      </div>
    );
  }

  return (
    <div className="defense-builder-page">
      <div className="builder-area">
        <p className="builder-instructions">
          Click a defender to select him, then choose his coverage assignment from the sidebar.
        </p>
        <DefenseBuilderField
          assignments={assignments}
          selectedPosition={selectedPosition}
          onSelectPosition={setSelectedPosition}
          formation={formation}
        />
      </div>

      <aside className="builder-panel">
        <h2>Defense Builder</h2>

        <div className="builder-section">
          {/* A <span>, not a <label>: FormationSelector's trigger is a div, which htmlFor cannot
              target. Named via aria-labelledby instead. */}
          <span className="builder-label" id="builder-formation-label">Formation</span>
          <FormationSelector
            id="builder-formation-select"
            labelledBy="builder-formation-label"
            selectedFormation={formation}
            onFormationChange={setFormation}
          />
        </div>

        <div className="builder-section builder-section-selected">
          <span className="builder-label">
            Selected Position: <span className="selected-position-name">{DEFENSE_POSITION_LABELS[selectedPosition]}</span>
          </span>
          <PositionAssignmentSelector
            id="builder-assignment-select"
            position={selectedPosition}
            selectedCoverage={assignments[selectedPosition]}
            onCoverageChange={handleAssignmentChange}
          />
          {selectedOption && !selectedOption.isImplemented && (
            <p className="builder-warning">
              ⚠️ This assignment doesn't have movement logic built yet — {DEFENSE_POSITION_LABELS[selectedPosition]} will hold his alignment during simulation.
            </p>
          )}
        </div>

        <div className="builder-section">
          <span className="builder-label">All Assignments</span>
          <ul className="assignment-summary-list">
            {DEFENSE_POSITION_IDS.map((position) => {
              const coverage = assignments[position];
              const option = getAssignmentOption(position, coverage);
              return (
                <li
                  key={position}
                  className={`assignment-summary-item ${position === selectedPosition ? 'active' : ''}`}
                  onClick={() => setSelectedPosition(position)}
                >
                  <span className="summary-position">{DEFENSE_POSITION_LABELS[position]}</span>
                  <span className="summary-coverage">
                    {option?.label ?? coverage}
                    {option && !option.isImplemented && <span className="summary-hold-dot" title="Holds alignment" />}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="builder-section">
          <CustomDefenseManager
            currentDefense={currentDefense}
            onLoadDefense={handleLoadDefense}
            saveTarget={defenseSaveTarget}
          />
        </div>
      </aside>
    </div>
  );
}

export default DefenseBuilderPage;
